"use client";

import React, { useState, useTransition, useEffect } from "react";
import QRCode from "qrcode";
import { Code } from "@/components/ui/code";
import { verifyMfaChallenge } from "../../../app/actions/auth-custom";
import ErrorAlert from "./ErrorAlert";
import { SSO_REQUIRED_ERROR } from "../../lib/auth-constants";
import { captureProductClientEvent } from "@/lib/analytics/product-client-event";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { normalizeAuthError } from "@/lib/analytics/auth-errors";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Note } from "@/components/ui/note";

interface MfaEnrollmentFormProps {
  pendingToken: string;
  challengeId: string;
  totpUri: string;
  totpSecret: string;
  onSuccess: () => void;
  /** BRE-8618: where an org's own sign-in returns to, if this step is sent there. */
  returnTo: string | undefined;
  /** BRE-8955: the org requires SSO; the parent goes back to where the SSO button is. */
  onSsoRequired?: () => void;
}

export default function MfaEnrollmentForm({
  pendingToken,
  challengeId,
  totpUri,
  totpSecret,
  onSuccess,
  returnTo,
  onSsoRequired,
}: MfaEnrollmentFormProps) {
  const [isPending, startTransition] = useTransition();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  useEffect(() => {
    QRCode.toDataURL(totpUri, { width: 200, margin: 2 })
      .then(setQrDataUrl)
      .catch(() => {
        // If QR generation fails, user can still use the manual secret
        setQrDataUrl(null);
      });
  }, [totpUri]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    startTransition(async () => {
      const result = await verifyMfaChallenge(code, pendingToken, challengeId, returnTo);
      if (result.success) {
        captureProductClientEvent(ANALYTICS_EVENTS.AUTH_MFA_VERIFIED, { mfa_type: "enrollment" });
        onSuccess();
      } else if ("signInUrl" in result && result.signInUrl) {
        // BRE-8618: the org this sign-in lands in requires its own sign-in (SSO).
        window.location.assign(result.signInUrl);
      } else if ("error" in result) {
        captureProductClientEvent(ANALYTICS_EVENTS.AUTH_MFA_FAILED, { mfa_type: "enrollment", error_code: normalizeAuthError(result.error) });
        if (result.error === SSO_REQUIRED_ERROR && onSsoRequired) {
          onSsoRequired();
          return;
        }
        setError(result.error);
      }
    });
  }

  return (
    <div className="space-y-4">
      <Note size="sm">
        Scan this QR code with your authenticator app, then enter the 6-digit code below.
      </Note>

      <div className="flex justify-center">
        {qrDataUrl ? (
          <img
            src={qrDataUrl}
            alt="Scan this QR code with your authenticator app"
            width={200}
            height={200}
            className="rounded-lg border border-border"
          />
        ) : (
          <div className="w-[200px] h-[200px] rounded-lg border border-border flex items-center justify-center">
            <Note as="span" size="sm">Loading QR code...</Note>
          </div>
        )}
      </div>

      <details className="text-xs text-muted-foreground">
        <summary className="cursor-pointer hover:text-foreground">
          Can&apos;t scan? Enter this key manually
        </summary>
        <Code variant="block" className="mt-2 whitespace-pre-wrap break-all text-center">
          {totpSecret}
        </Code>
      </details>

      {error && <ErrorAlert message={error} />}

      <form onSubmit={handleSubmit} className="space-y-3">
        <label htmlFor="mfa-enroll-code" className="sr-only">
          Authenticator code
        </label>
        <Input
          textShortcuts={false}
          id="mfa-enroll-code"
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="Enter 6-digit code"
          required
          inputMode="numeric"
          pattern="^\d{6}$"
          autoComplete="one-time-code"
          autoFocus
          testId="mfa-enroll-code-input"
          variant="onboarding"
          fontSize="sm"
          inputClassName="px-0 text-center tracking-[0.5em] font-mono"
        />
        <Button
          type="submit"
          disabled={isPending || code.length !== 6}
          data-testid="mfa-enroll-verify-button"
          className="w-full"
        >
          {isPending ? "Verifying..." : "Verify and continue"}
        </Button>
      </form>
    </div>
  );
}
